import React, { useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { BodyM, Caption } from 'components/Typography/Typography';
import DialogTermsAndConditions from 'views/Numbers/Details/Services/subcomponents/common/DialogTermsAndConditions';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/servicesStyle';
import { makeStyles } from '@material-ui/core';
import InternationalMinutesTable from 'components/ServicesTables/InternationalMinutesTable';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import VatClause from 'components/VatClause/VatClause';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IActivateInternationalMinutesDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	termsAndConditions: types.services.termsAndConditions;
	catalogEntryView: types.ecommerce.CatalogEntryView[];
	updateOrderItems: types.services.updateOrderItemsFunction;
	loading: boolean;
	countries: string[];
	activeCatalogEntryView: types.ecommerce.CatalogEntryView[] | undefined;
	header: string;
	buttonText: string;
	additionalText: string;
}

const ActivateInternationalMinutesDialog = ({
	isOpen,
	hideDialog,
	termsAndConditions,
	catalogEntryView,
	updateOrderItems,
	loading,
	countries,
	activeCatalogEntryView,
	header,
	buttonText,
	additionalText,
}: IActivateInternationalMinutesDialogProps) => {
	const classes = useStyles();
	const [selectedTab, setSelectedTab] = useState<number | null>(null);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [showError, setShowError] = useState<boolean>(false);

	const activationButtonDisabled = useMemo(() => {
		if (selectedTab) {
			const selectedCatalogEntryView = catalogEntryView[selectedTab];
			if (activeCatalogEntryView)
				if (
					activeCatalogEntryView &&
					selectedCatalogEntryView &&
					activeCatalogEntryView.some((acev) => acev.partNumber === selectedCatalogEntryView.partNumber)
				) {
					return true;
				}
		}
		return selectedTab === null || selectedTab < 0 || (showError && !isChecked);
	}, [selectedTab, activeCatalogEntryView, catalogEntryView, showError, isChecked]);

	const activateInternationalMinutes = () => {
		if (!isChecked) {
			setShowError(true);
		} else {
			setShowError(false);
			if (selectedTab !== null && selectedTab >= 0) {
				hideDialog();
				const selectedCatalogEntryView = catalogEntryView[selectedTab];
				const place = extractAttributeValue(
					{ attributes: selectedCatalogEntryView.attributes },
					CustomAttribute.DEFINITION_SOURCE
				);
				const ebuConfigProduct = extractAttributeValue(
					{ attributes: selectedCatalogEntryView.attributes },
					CustomAttribute.EBU_CONFIG_PRODUCT
				);
				const orderItems: types.services.IOrderItem[] = [
					{
						action: 'ADD',
						id: selectedCatalogEntryView.partNumber,
						place: place,
						ebuConfigProduct,
					},
				];
				updateOrderItems(orderItems, 'TURN_ON', selectedCatalogEntryView.name, true);
			}
		}
	};

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			width={1000}
			loading={loading}
			header={translate('number.services.international.minutes.activation')}
			dialogBody={
				<div>
					<div className={classes.internationalMinutesBodyWrapper}>
						<BodyM disableMargin color="secondary-light">
							{translate('number.services.international.select.package')}
						</BodyM>
					</div>
					<InternationalMinutesTable
						catalogEntryView={catalogEntryView}
						selectedTab={selectedTab}
						setSelectedTab={setSelectedTab}
					/>
					<div className={classes.internationalMinutesBodyWrapper}>
						<ConditionalRender
							show={countries.length > 0}
							onTrue={() => (
								<div className={classes.relative}>
									<BodyM disableMargin color="secondary-light">
										{translate('services.international.minutes.country.on.the.list')}
									</BodyM>
									<div className={classes.countriesWrapper}>
										<div className={classes.countries}>
											{countries.map((country) => (
												<div key={country}>
													<Caption inline disableMargin>
														{country}
													</Caption>
												</div>
											))}
										</div>
									</div>
									<div className={classes.countriesFog}></div>
								</div>
							)}
						/>
						<div>
							<VatClause />
							<DialogTermsAndConditions
								termsAndConditions={termsAndConditions}
								isChecked={isChecked}
								setIsChecked={setIsChecked}
								showError={showError}
								showCheckbox
							/>
						</div>
					</div>
				</div>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: hideDialog,
				},
				{
					text: translate('add'),
					color: 'primary-light',
					disabled: activationButtonDisabled,
					onClick: activateInternationalMinutes,
				},
			]}
		/>
	);
};

export default ActivateInternationalMinutesDialog;
